import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { Api } from '@/core/api.js'

import es from './es.json'
import en from './en.json'
import pt from './pt.json'
import de from './de.json'
import fr from './fr.json'
import it from './it.json'
import init from './init.json'
const api = new Api()

const IS_APP = process.env.VUE_APP_IS_APP === 'TRUE'
const savedLanguage = localStorage.getItem('language')
const defaultLocale = savedLanguage && savedLanguage !== 'init' && savedLanguage !== '' ? savedLanguage : 'init'

moment.updateLocale('es', {
    calendar: {
        lastDay: '[Ayer]',
        sameDay: '[Hoy]',
        nextDay: '[Mañana]',
        lastWeek: '[El pasado] dddd',
        nextWeek: '[La próxima semana]',
        sameElse: 'dddd, DD [de] MMMM [del] YYYY'
    }
})
moment.updateLocale('en', {
    calendar: {
        lastDay: '[Yesterday]',
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        lastWeek: '[Last] dddd',
        nextWeek: '[The next week]',
        sameElse: 'dddd, DD [of] MMMM [of] YYYY'
    }
})
moment.updateLocale('pt', {
    calendar: {
        lastDay: '[Ontem]',
        sameDay: '[Hoje]',
        nextDay: '[Manhã]',
        lastWeek: 'dddd [passada]',
        nextWeek: '[A próxima semana]',
        sameElse: 'dddd, DD [de] MMMM [de] YYYY'
    }
})
moment.updateLocale('de', {
    calendar: {
        lastDay: '[Gestern]',
        sameDay: '[Heute]',
        nextDay: '[Morgen]',
        lastWeek: '[Letzter] dddd',
        nextWeek: '[Nächste Woche]',
        sameElse: 'dddd, DD. MMMM YYYY'
    }
})
moment.updateLocale('fr', {
    calendar: {
        lastDay: '[Hier]',
        sameDay: "[Aujourd'hui]",
        nextDay: '[Demain]',
        lastWeek: '[La semaine dernière] dddd',
        nextWeek: '[La semaine prochaine]',
        sameElse: 'dddd, DD [de] MMMM [de] YYYY'
    }
})
moment.locale(defaultLocale)

Vue.use(VueI18n)

const bundledMessages = { init, es, en, pt, de, fr, it }

let languages = {
    init: init
}
let loadedLanguages = ['init']

/**
 * El API de idioma sustituye el JSON local. Las claves nuevas de FT-116
 * desaparecían y caían al fallback `init` (ES), mezclando idiomas
 * (`Stornieren` + “Imagen del producto”).
 *
 * @param {Object} base
 * @param {Object} override
 * @returns {Object}
 */
function mergeLocaleMessages(base, override) {
    if (!override || typeof override !== 'object' || Array.isArray(override)) {
        return base && typeof base === 'object' ? base : {}
    }
    if (!base || typeof base !== 'object' || Array.isArray(base)) {
        return override
    }

    const merged = { ...base }
    Object.keys(override).forEach((key) => {
        const baseValue = merged[key]
        const overrideValue = override[key]
        if (
            baseValue &&
            overrideValue &&
            typeof baseValue === 'object' &&
            typeof overrideValue === 'object' &&
            !Array.isArray(baseValue) &&
            !Array.isArray(overrideValue)
        ) {
            merged[key] = mergeLocaleMessages(baseValue, overrideValue)
            return
        }
        merged[key] = overrideValue
    })
    return merged
}

if (IS_APP) {
    loadedLanguages.push('es')
    loadedLanguages.push('pt')
    loadedLanguages.push('en')
    loadedLanguages.push('de')
    loadedLanguages.push('fr')
    loadedLanguages.push('it')

    languages.es = es
    languages.pt = pt
    languages.en = en
    languages.de = de
    languages.fr = fr
    languages.it = it
}
const i18n = new VueI18n({
    //   locale: defaultLocale,
    locale: 'init',
    fallbackLocale: 'init',
    messages: Object.assign(languages),
    silentTranslationWarn: true
})

const loadingLanguagePromises = {}
let requestedLanguage = null

function applyLanguageIfCurrent(lang) {
    if (requestedLanguage !== lang) {
        return requestedLanguage
    }
    return setI18nLanguage(lang)
}

function fallbackLanguageOnLoadError(lang) {
    if (requestedLanguage !== lang) {
        return false
    }

    if (i18n.messages[lang] && Object.keys(i18n.messages[lang]).length > 0) {
        return setI18nLanguage(lang)
    }

    requestedLanguage = i18n.locale
    if (i18n.locale && i18n.locale !== 'init') {
        localStorage.setItem('language', i18n.locale)
    }
    return false
}

function loadLanguageFromApi(lang) {
    if (loadingLanguagePromises[lang]) {
        return loadingLanguagePromises[lang]
    }

    loadingLanguagePromises[lang] = api
        .language(lang)
        .then((response) => {
            log(typeof response)
            if (typeof response === 'string') {
                response = JSON.parse(response)
            }
            i18n.setLocaleMessage(lang, mergeLocaleMessages(bundledMessages[lang] || {}, response))
            if (!loadedLanguages.includes(lang)) {
                loadedLanguages.push(lang)
            }
            return applyLanguageIfCurrent(lang)
        })
        .catch((error) => {
            logError(error)
            return fallbackLanguageOnLoadError(lang)
        })
        .finally(() => {
            delete loadingLanguagePromises[lang]
        })

    return loadingLanguagePromises[lang]
}

i18n.loadLanguageAsync = function (lang, force = false) {
    requestedLanguage = lang
    const hasConnection = fnCheckConnection()

    if (i18n.locale !== lang || (force && hasConnection && lang !== 'init')) {
        if (!loadedLanguages.includes(lang) || hasConnection) {
            return loadLanguageFromApi(lang)
        }
        return Promise.resolve(applyLanguageIfCurrent(lang))
    } else if (hasConnection && lang === 'init') {
        const savedLang = localStorage.getItem('language')
        if (!savedLang) {
            return Promise.resolve('init')
        }
        lang = savedLang
        requestedLanguage = lang
        return loadLanguageFromApi(lang)
    }

    return Promise.resolve(lang)
}

i18n.loadLanguageAsync(defaultLocale)

export default i18n

function setI18nLanguage(lang) {
    log('setI18nLanguage', lang)
    i18n.locale = lang
    moment.locale(lang)
    document.querySelector('html').setAttribute('lang', lang)
    log(i18n.messages)
    return lang
}

function parseLocaleMessages(response, lang) {
    if (typeof response === 'string') {
        response = JSON.parse(response)
    }
    if (!isLocaleMessageDictionary(response)) {
        throw new Error('Invalid language response for ' + lang)
    }
    return response
}

function isLocaleMessageDictionary(response) {
    return response && typeof response === 'object' && !Array.isArray(response) && typeof response.login === 'object' && typeof response.menu_tools === 'object' && typeof response.common === 'object'
}
